"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/app/_module/components/ui/card";
import { Button } from "@/app/_module/components/ui/button";
import { Badge } from "@/app/_module/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/app/_module/components/ui/dialog";
import { useToast } from "@/app/_module/hooks/use-toast";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { useUserPhoneNumbers } from "@/app/_module/hooks/usePhoneNumberPricing";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { formatPhoneNumber } from "@/app/_module/utils/phoneUtils";
import {
  DIALER_SYSTEMS,
  carrierOf,
  otherSystem,
  switchPlan,
  type DialerCarrier,
} from "@/app/_module/utils/dialerSystem";
import { updateUser } from "../../services/user";

type PortRequest = { id: string; status: string; focDate?: string | null; numbers?: string[] };

const PORT_STATUS: Record<string, string> = {
  requested: "Requested. Our team is preparing it.",
  submitted: "In progress with the carriers.",
  foc_scheduled: "Scheduled",
  ported: "Done. Your numbers are on InsuraDialer Next.",
  exception: "Needs attention. Our team will contact you.",
  cancelled: "Cancelled.",
};

/** Settings, Dialer: which InsuraDialer system the agent's calls run on, and the move to Next. */
export function DialerSystemCard() {
  const { user, refetchUser } = useUserContext();
  const { toast } = useToast();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { data: numbers = [] } = useUserPhoneNumbers();
  const [target, setTarget] = useState<DialerCarrier | null>(null);
  const [busy, setBusy] = useState(false);

  const u = user?.user as any;
  const nextAvailable = !!u?.nextDialerAvailable;
  const { data: port } = useQuery({
    queryKey: ["number-ports", "mine"],
    queryFn: async () => {
      const res: any = await api.get({ url: getApiUrl("/number-ports/mine"), auth: true });
      return (res?.data?.data ?? res?.data ?? null) as PortRequest | null;
    },
    enabled: nextAvailable,
    refetchInterval: 60_000,
  });
  if (!nextAvailable) return null;

  const current = carrierOf(u?.voiceProvider);
  const plan = target ? switchPlan(target, numbers as any[]) : null;
  const portOpen = !!port && !["ported", "cancelled"].includes(port.status);

  const switchNow = async () => {
    if (!target || !plan?.useNumber) return;
    setBusy(true);
    try {
      await updateUser(u.id, { defaultOutboundPhone: plan.useNumber } as any, { silent: true });
      refetchUser((prev: any) => ({
        ...prev,
        user: { ...prev?.user, voiceProvider: target, defaultOutboundPhone: plan.useNumber },
      }));
      window.dispatchEvent(new Event("dialer:carrier-check"));
      toast({ title: `You're on ${DIALER_SYSTEMS[target].name}`, description: `Calls go out from ${formatPhoneNumber(plan.useNumber)}.` });
      setTarget(null);
    } catch {
      toast({ title: "Couldn't switch", description: "Please try again.", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const requestTransfer = async () => {
    setBusy(true);
    try {
      const res: any = await api.post({ url: getApiUrl("/number-ports"), body: {}, auth: true });
      await queryClient.invalidateQueries({ queryKey: ["number-ports", "mine"] });
      toast({
        title: "Transfer requested",
        description: `${res?.data?.message ?? "We'll move your numbers to InsuraDialer Next."} Progress shows on this page.`,
      });
      setTarget(null);
    } catch (e: any) {
      toast({ title: "Couldn't request the transfer", description: e?.message || "Please try again.", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="overflow-hidden mb-6">
      <CardContent className="p-6 space-y-4">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Dialer system</h2>
          <p className="text-sm text-muted-foreground">
            Your calls run on <strong>{DIALER_SYSTEMS[current].name}</strong>. It follows your default number.
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {(["twilio", "telnyx"] as DialerCarrier[]).map((c) => (
            <div key={c} className={`rounded-lg border p-4 ${c === current ? "border-primary bg-primary/5" : ""}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="font-medium">{DIALER_SYSTEMS[c].name}</p>
                {c === current ? (
                  <Badge>Current</Badge>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => setTarget(c)}>
                    Switch to {DIALER_SYSTEMS[c].short}
                  </Button>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-1">{DIALER_SYSTEMS[c].blurb}</p>
            </div>
          ))}
        </div>
        {port && (
          <p className="text-sm rounded border bg-muted/40 p-3">
            <strong>Number transfer to Next:</strong> {PORT_STATUS[port.status] ?? port.status}
            {port.status === "foc_scheduled" && port.focDate && ` for ${new Date(port.focDate).toLocaleDateString()}.`}
          </p>
        )}
      </CardContent>

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        {target && plan && (
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Switch to {DIALER_SYSTEMS[target].name}?</DialogTitle>
              <DialogDescription>
                {plan.useNumber
                  ? `Calls will go out from ${formatPhoneNumber(plan.useNumber)}. It becomes your default number.`
                  : `You don't have a number on ${DIALER_SYSTEMS[target].name} yet.`}
              </DialogDescription>
            </DialogHeader>
            {plan.leftBehind > 0 && (
              <div className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded p-3 space-y-2">
                <p>
                  {plan.leftBehind} of your numbers {plan.leftBehind === 1 ? "is" : "are"} on{" "}
                  {DIALER_SYSTEMS[otherSystem(target)].name}. {plan.leftBehind === 1 ? "It" : "They"} can&apos;t make or
                  take calls on {DIALER_SYSTEMS[target].name} unless you transfer {plan.leftBehind === 1 ? "it" : "them"}.
                </p>
                {target === "telnyx" && (
                  <p>
                    Texting on InsuraDialer Next needs its own A2P registration, transferred numbers included. Until
                    it&apos;s approved, those numbers can&apos;t send texts on Next.
                  </p>
                )}
              </div>
            )}
            <DialogFooter className="gap-2 sm:gap-2 flex-wrap">
              <Button variant="ghost" onClick={() => setTarget(null)} disabled={busy}>
                Cancel
              </Button>
              {target === "telnyx" && plan.leftBehind > 0 && !portOpen && (
                <Button variant="outline" onClick={requestTransfer} disabled={busy}>
                  Transfer my numbers
                </Button>
              )}
              {plan.useNumber ? (
                <Button onClick={switchNow} disabled={busy}>
                  Switch to {DIALER_SYSTEMS[target].short}
                </Button>
              ) : (
                <Button
                  onClick={() => router.push(`/settings?tab=area-codes&areaCodeTab=purchase&system=${target}`)}
                  disabled={busy}
                >
                  Get a {DIALER_SYSTEMS[target].short} number
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </Card>
  );
}
